import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Loader2, Activity, AlertOctagon, Building2, KeyRound } from "lucide-react";

const RANGES = [
  { id: "24h", label: "24h" },
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
];

function fmt(n) {
  if (n === null || n === undefined) return "—";
  if (typeof n === "number" && n >= 1000) return n.toLocaleString();
  return String(n);
}

function fmtBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = Number(n);
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

export default function MasterApiUsagePage() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const r = await api.get("/master/api-keys/usage-summary", { params: { range } });
        setData(r.data);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [range]);

  const totalReq = (data?.tenants || []).reduce((s, t) => s + (t.requests || 0), 0);
  const totalErr = (data?.tenants || []).reduce((s, t) => s + (t.errors || 0), 0);
  const totalBytesOut = (data?.tenants || []).reduce((s, t) => s + (t.bytes_out || 0), 0);

  return (
    <div className="space-y-6" data-testid="master-api-usage-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
            Platform admin · Observability
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">
            API usage overview
          </h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Workspace-wide volume + error posture for every active API key on the platform.
          </p>
        </div>
        <ToggleGroup
          type="single"
          value={range}
          onValueChange={(v) => v && setRange(v)}
          data-testid="master-usage-range-toggle"
        >
          {RANGES.map((r) => (
            <ToggleGroupItem key={r.id} value={r.id} data-testid={`master-usage-range-${r.id}`}>
              {r.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {loading && (
        <div className="flex justify-center py-16" data-testid="master-usage-loading">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && error && (
        <Card className="border-destructive/40">
          <CardContent className="py-8 text-center text-destructive">{error}</CardContent>
        </Card>
      )}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="master-usage-kpis">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                  <Activity className="h-4 w-4" /> Platform requests
                </div>
                <div className="mt-2 font-display text-2xl font-semibold text-primary tabular-nums">
                  {fmt(totalReq)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  across {data.tenants.length} workspaces
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                  <AlertOctagon className="h-4 w-4" /> Errors
                </div>
                <div className="mt-2 font-display text-2xl font-semibold text-primary tabular-nums">
                  {fmt(totalErr)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {totalReq ? `${((totalErr / totalReq) * 100).toFixed(2)}% error rate` : "—"}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                  Bytes egressed
                </div>
                <div className="mt-2 font-display text-2xl font-semibold text-primary tabular-nums">
                  {fmtBytes(totalBytesOut)}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Workspaces
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workspace</TableHead>
                    <TableHead className="text-right">Active keys</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Errors</TableHead>
                    <TableHead className="text-right">Error rate</TableHead>
                    <TableHead className="text-right">Bytes out</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="master-tenant-usage-tbody">
                  {data.tenants.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                        No usage in this window yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {data.tenants.map((t) => (
                    <TableRow key={t.tenant_id} data-testid={`master-tenant-row-${t.tenant_id}`}>
                      <TableCell className="font-mono text-xs">{t.tenant_id.slice(0, 8)}…</TableCell>
                      <TableCell className="text-right tabular-nums">{t.active_keys}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(t.requests)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(t.errors)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {t.requests ? `${(t.error_rate * 100).toFixed(2)}%` : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmtBytes(t.bytes_out)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <KeyRound className="h-4 w-4" /> Top 10 API keys by volume
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Prefix</TableHead>
                    <TableHead>Workspace</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Errors</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="master-top-keys-tbody">
                  {(data.top_keys || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                        No key traffic yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {(data.top_keys || []).map((k) => (
                    <TableRow key={k.api_key_id} data-testid={`master-top-key-row-${k.api_key_id}`}>
                      <TableCell className="font-medium">{k.key_name || "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {k.key_prefix}
                      </TableCell>
                      <TableCell className="text-xs">
                        {k.tenant_slug ? (
                          <Badge variant="outline" className="rounded-full">{k.tenant_slug}</Badge>
                        ) : (
                          <span className="font-mono">{k.tenant_id.slice(0, 8)}…</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(k.requests)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(k.errors)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm" variant="outline" asChild
                          data-testid={`master-top-key-view-${k.api_key_id}`}
                        >
                          <Link to={`/master/api-keys/${k.api_key_id}/usage`}>
                            View
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
